import { passwordStrength } from "check-password-strength";


document.addEventListener("DOMContentLoaded", () => {

    const signupForm =
        document.querySelector("#signupForm");

    if (!signupForm) {
        return;
    }


    /*
     * ----------------------------------------------------------
     * Elements
     * ----------------------------------------------------------
     */

    const passwordInput =
        signupForm.querySelector("#password");

    const confirmPasswordInput =
        signupForm.querySelector("#confirmPassword");

    const strengthWrapper =
        signupForm.querySelector(
            "[data-password-strength]"
        );

    const strengthBar =
        strengthWrapper?.querySelector(
            "[data-password-strength-bar]"
        );

    const strengthLabel =
        strengthWrapper?.querySelector(
            "[data-password-strength-label]"
        );

    const strengthProgress =
        strengthWrapper?.querySelector(
            ".progress"
        );

    const requirementsWrapper =
        signupForm.querySelector(
            "[data-password-requirements]"
        );

    const passwordMatch =
        signupForm.querySelector(
            "[data-password-match]"
        );

    const confirmPasswordFeedback =
        confirmPasswordInput
            ?.closest(".form-floating")
            ?.querySelector(".invalid-feedback") ??
        null;

    const confirmPasswordRequiredText =
        confirmPasswordFeedback?.textContent.trim() ??
        "";


    /*
     * ----------------------------------------------------------
     * Password visibility
     * ----------------------------------------------------------
     */

    signupForm
        .querySelectorAll("[data-password-toggle]")
        .forEach((button) => {

            button.addEventListener("click", () => {

                const selector =
                    button.dataset.passwordToggle;

                const input =
                    signupForm.querySelector(selector);

                if (!input) {
                    return;
                }

                const shouldShow =
                    input.type === "password";

                input.type =
                    shouldShow
                        ? "text"
                        : "password";

                const icon =
                    button.querySelector("i");

                icon?.classList.toggle(
                    "bi-eye",
                    !shouldShow
                );

                icon?.classList.toggle(
                    "bi-eye-slash",
                    shouldShow
                );

                const label =
                    shouldShow
                        ? button.dataset.hideLabel
                        : button.dataset.showLabel;

                if (label) {
                    button.setAttribute(
                        "aria-label",
                        label
                    );
                }

            });

        });


    /*
     * ----------------------------------------------------------
     * Password Strength
     * ----------------------------------------------------------
     */

    const resetStrength = () => {

        if (!strengthBar || !strengthLabel) {
            return;
        }

        strengthBar.style.width = "0%";

        strengthBar.classList.remove(
            "bg-danger",
            "bg-warning",
            "bg-info",
            "bg-success"
        );

        strengthLabel.textContent =
            strengthLabel.dataset.emptyLabel ??
            "Enter a password";

        strengthProgress?.setAttribute(
            "aria-valuenow",
            "0"
        );

    };


    const updatePasswordStrength = () => {

        if (
            !passwordInput ||
            !strengthBar ||
            !strengthLabel
        ) {
            return;
        }

        const password =
            passwordInput.value;

        if (!password) {
            resetStrength();
            return;
        }

        const result =
            passwordStrength(password);

        const strengthMap = {
            0: {
                width: 25,
                className: "bg-danger",
                label:
                    strengthLabel.dataset.tooWeakLabel ??
                    "Too weak"
            },

            1: {
                width: 50,
                className: "bg-warning",
                label:
                    strengthLabel.dataset.weakLabel ??
                    "Weak"
            },

            2: {
                width: 75,
                className: "bg-info",
                label:
                    strengthLabel.dataset.mediumLabel ??
                    "Medium"
            },

            3: {
                width: 100,
                className: "bg-success",
                label:
                    strengthLabel.dataset.strongLabel ??
                    "Strong"
            }
        };

        const strength =
            strengthMap[result.id] ??
            strengthMap[0];

        strengthBar.classList.remove(
            "bg-danger",
            "bg-warning",
            "bg-info",
            "bg-success"
        );

        strengthBar.classList.add(
            strength.className
        );

        strengthBar.style.width =
            `${strength.width}%`;

        strengthLabel.textContent =
            strength.label;

        strengthProgress?.setAttribute(
            "aria-valuenow",
            String(strength.width)
        );

    };


    /*
     * ----------------------------------------------------------
     * Password Requirements
     * ----------------------------------------------------------
     */

    const updatePasswordRequirements = () => {

        if (
            !passwordInput ||
            !requirementsWrapper
        ) {
            return;
        }

        const password =
            passwordInput.value;

        const requirements = {
            length:
                password.length >= 8,

            lowercase:
                /[a-z]/.test(password),

            uppercase:
                /[A-Z]/.test(password),

            number:
                /\d/.test(password),

            special:
                /[^A-Za-z0-9]/.test(password)
        };

        Object.entries(requirements)
            .forEach(([name, isValid]) => {

                const requirement =
                    requirementsWrapper.querySelector(
                        `[data-password-requirement="${name}"]`
                    );

                if (!requirement) {
                    return;
                }

                requirement.classList.toggle(
                    "is-valid",
                    isValid
                );

                const icon =
                    requirement.querySelector("i");

                if (!icon) {
                    return;
                }

                icon.classList.toggle(
                    "bi-circle",
                    !isValid
                );

                icon.classList.toggle(
                    "bi-check-circle-fill",
                    isValid
                );

            });

    };


    /*
     * ----------------------------------------------------------
     * Password Match
     * ----------------------------------------------------------
     */

    const updatePasswordMatch = () => {

        if (
            !passwordInput ||
            !confirmPasswordInput
        ) {
            return;
        }

        const password =
            passwordInput.value;

        const confirmation =
            confirmPasswordInput.value;

        if (!confirmation) {

            confirmPasswordInput
                .setCustomValidity("");

            confirmPasswordInput
                .classList.remove(
                    "is-valid",
                    "is-invalid"
                );

            if (confirmPasswordFeedback) {
                confirmPasswordFeedback.textContent =
                    confirmPasswordRequiredText;
            }

            if (passwordMatch) {
                passwordMatch.textContent = "";

                passwordMatch.classList.remove(
                    "text-success",
                    "text-danger"
                );
            }

            return;
        }

        const matches =
            password === confirmation;

        const errorLabel =
            passwordMatch?.dataset.errorLabel ??
            "Passwords do not match.";

        confirmPasswordInput.setCustomValidity(
            matches
                ? ""
                : errorLabel
        );

        confirmPasswordInput.classList.toggle(
            "is-valid",
            matches
        );

        confirmPasswordInput.classList.toggle(
            "is-invalid",
            !matches
        );

        /*
         * The invalid-feedback div is the single visible error
         * message; the live indicator below is success-only so
         * the mismatch text isn't shown in two places at once.
         */

        if (confirmPasswordFeedback) {
            confirmPasswordFeedback.textContent =
                matches
                    ? confirmPasswordRequiredText
                    : errorLabel;
        }

        if (!passwordMatch) {
            return;
        }

        passwordMatch.textContent =
            matches
                ? passwordMatch.dataset.matchLabel ??
                    "Passwords match."
                : "";

        passwordMatch.classList.toggle(
            "text-success",
            matches
        );

        passwordMatch.classList.remove(
            "text-danger"
        );

    };


    /*
     * ----------------------------------------------------------
     * Password Policy
     * ----------------------------------------------------------
     */

    const passwordMeetsRequirements = () => {

        if (!passwordInput) {
            return false;
        }

        const password =
            passwordInput.value;

        return (
            password.length >= 8 &&
            /[a-z]/.test(password) &&
            /[A-Z]/.test(password) &&
            /\d/.test(password) &&
            /[^A-Za-z0-9]/.test(password)
        );

    };


    const updatePasswordValidity = () => {

        if (!passwordInput) {
            return;
        }

        /*
         * Keep HTML required validation intact when empty.
         */

        if (!passwordInput.value) {
            passwordInput.setCustomValidity("");
            return;
        }

        passwordInput.setCustomValidity(
            passwordMeetsRequirements()
                ? ""
                : "Password does not meet the required format."
        );

    };


    /*
     * ----------------------------------------------------------
     * Events
     * ----------------------------------------------------------
     */

    passwordInput?.addEventListener(
        "input",
        () => {

            updatePasswordStrength();

            updatePasswordRequirements();

            updatePasswordValidity();

            updatePasswordMatch();

        }
    );


    confirmPasswordInput?.addEventListener(
        "input",
        updatePasswordMatch
    );

    /*
     * ----------------------------------------------------------
     * Initial State
     * ----------------------------------------------------------
     */

    resetStrength();

});